import { Palette, Share2, TrendingUp, PenTool, Video, Search } from 'lucide-react';
import { PillarPage } from './PillarPage';

const services = [
  {
    icon: Palette,
    title: 'Graphic Design & Brand Identity',
    description:
      'Logos, brand systems, and marketing collateral that make you unmistakable — a cohesive visual language applied consistently across every touchpoint.',
    highlights: [
      'Logo & complete brand identity systems',
      'Social, print & ad creative design',
      'Brand guidelines & asset libraries',
      'Packaging, pitch decks & marketing collateral',
    ],
  },
  {
    icon: Share2,
    title: 'Social Media Management',
    description:
      'End-to-end management of your social presence — strategy, content calendars, community engagement, and growth across every platform that matters to you.',
    highlights: [
      'Content strategy & monthly calendars',
      'Instagram, TikTok, LinkedIn, X & Facebook',
      'Community management & engagement',
      'Monthly reporting & growth analytics',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Performance Marketing',
    description:
      'Paid campaigns engineered for ROI — from Meta and Google Ads to full-funnel strategy, tracking, and relentless optimization toward your target CPA.',
    highlights: [
      'Meta, Google, TikTok & LinkedIn Ads',
      'Full-funnel strategy & audience targeting',
      'Conversion tracking & pixel setup',
      'A/B testing & CPA / ROAS optimization',
    ],
  },
  {
    icon: PenTool,
    title: 'Content Creation & Copywriting',
    description:
      'Words and stories that convert — website copy, ad scripts, blog articles, and email sequences written to match your brand voice and drive action.',
    highlights: [
      'Website & landing-page copy',
      'Blog articles & SEO content',
      'Ad copy & campaign scripts',
      'Email newsletters & sequences',
    ],
  },
  {
    icon: Video,
    title: 'Video & Motion Graphics',
    description:
      'Scroll-stopping video and motion content — from short-form reels and product ads to explainer animations that bring your brand to life.',
    highlights: [
      'Short-form reels & TikTok videos',
      'Product ads & promotional videos',
      'Motion graphics & animated explainers',
      'Video editing & post-production',
    ],
  },
  {
    icon: Search,
    title: 'SEO & Digital Growth',
    description:
      'Get found by the people already searching for you — technical SEO, content strategy, and local optimization that compounds organic traffic over time.',
    highlights: [
      'Technical & on-page SEO',
      'Keyword research & content strategy',
      'Local SEO & Google Business Profile',
      'Backlink building & authority growth',
    ],
  },
];

const process = [
  { step: '01', label: 'Discover', desc: 'We audit your brand, audience, and market — and align on goals and KPIs.' },
  { step: '02', label: 'Strategy', desc: 'A tailored channel, content, and campaign plan built around your objectives.' },
  { step: '03', label: 'Create',   desc: 'Design, copy, and creative production — on-brand and ready to launch.' },
  { step: '04', label: 'Launch',   desc: 'Campaigns go live with tracking, pixels, and analytics wired in from day one.' },
  { step: '05', label: 'Optimize', desc: 'We measure, test, and refine continuously to compound your results.' },
];

const faqs = [
  {
    question: 'What marketing services do you offer?',
    answer: 'We offer a full-service marketing suite: graphic design and brand identity, social media management, performance marketing (paid ads), content creation and copywriting, video and motion graphics, and SEO. You can engage us for a single service or as your end-to-end marketing partner.',
  },
  {
    question: 'How do you price marketing services?',
    answer: 'We offer both monthly retainers and project-based pricing. Retainers are ideal for ongoing work like social media management, content, and paid ads, while one-off projects like a brand identity or a campaign launch are priced per scope. We build a plan around your goals and budget after a discovery call.',
  },
  {
    question: 'Which social media platforms do you manage?',
    answer: 'We manage Instagram, TikTok, LinkedIn, X (Twitter), Facebook, and YouTube. We recommend the platforms that best fit your audience and objectives rather than spreading you thin across all of them.',
  },
  {
    question: 'What ad platforms do you run performance campaigns on?',
    answer: 'We run paid campaigns on Meta (Facebook & Instagram), Google Ads (Search, Display, Shopping, YouTube), TikTok Ads, and LinkedIn Ads. We set up conversion tracking and pixels, structure full-funnel campaigns, and optimize continuously toward your target CPA and ROAS.',
  },
  {
    question: 'Do you handle both strategy and execution?',
    answer: 'Yes. We are not just a strategy shop or just a production house — we do both. We define the strategy, then design, write, produce, and run everything, reporting on results and iterating along the way.',
  },
  {
    question: 'Can you design a brand from scratch?',
    answer: 'Absolutely. We build complete brand identities from the ground up — logo, color system, typography, brand guidelines, and a full asset library — so your brand is consistent and recognizable across every channel.',
  },
  {
    question: 'How soon will I see results from performance marketing?',
    answer: 'Paid campaigns can drive traffic and leads within days of launch, but reaching efficient, stable performance usually takes 4 to 8 weeks of testing and optimization. Organic channels like SEO and social growth compound over 3 to 6 months.',
  },
  {
    question: 'Do you provide reporting and analytics?',
    answer: 'Yes. Every engagement includes transparent reporting — monthly performance reports for social and SEO, and live dashboards for paid campaigns covering spend, CPA, ROAS, and conversions. You always know exactly what your marketing is delivering.',
  },
  {
    question: 'Can marketing be bundled with my website or Shopify store?',
    answer: 'Yes, and it works beautifully together. As a full technology and marketing partner we can build your Shopify store or website and drive traffic and sales to it — keeping design, tracking, and messaging aligned end-to-end.',
  },
];

export const MarketingServicesPage = () => (
  <PillarPage
    meta={{
      title: 'Marketing & Creative Agency',
      label: 'Marketing & Creative',
      headline: 'Marketing That Moves the Needle',
      italicWord: 'Moves the Needle',
      description:
        'A full-service marketing agency under one roof — graphic design, social media, performance marketing, content, video, and SEO — engineered to grow your brand and your bottom line.',
      servicesHeading: 'Everything Your Brand Needs to Grow',
    }}
    theme={{
      primary: '#DB2777',
      accent:  '#EC4899',
      bg:      '#FDF2F8',
      soft:    '#FDF2F8',
    }}
    services={services}
    process={process}
    cta={{
      headline: 'Ready to grow your brand?',
      italic:   "Let's make it happen.",
      buttonLabel: 'Start Your Campaign',
      bg: '#0A0A0A',
    }}
    seo={{
      title: 'Marketing Agency — Branding, Social Media, Ads & SEO',
      description: 'Full-service marketing agency offering graphic design, brand identity, social media management, performance marketing (Meta & Google Ads), content, video, and SEO to grow your business.',
      path: '/services/marketing',
      serviceName: 'Marketing & Creative Agency Services',
    }}
    faqs={faqs}
  />
);
